import { currencySchema } from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { sortedListRequest } from './common/list.js';
import { paginated } from './common/pagination.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

/**
 * A calendar day that exists. A bare `YYYY-MM-DD` pattern lets `2026-02-31`
 * through to Postgres, which refuses it as a raw error the API reports as a 500.
 */
const isoDaySchema = z.iso.date('a day is a real YYYY-MM-DD date');

const DAY_MS = 24 * 60 * 60 * 1000;

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
}

/** The longest range one backfill request may ask the TCMB archive for. */
export const MAX_EXCHANGE_RATE_BACKFILL_DAYS = 400;

export const EXCHANGE_RATE_SOURCES = ['tcmb', 'manual'] as const;
export const exchangeRateSourceSchema = z.enum(EXCHANGE_RATE_SOURCES);

export const EXCHANGE_RATE_BACKFILL_STATUSES = ['pending', 'running', 'done', 'failed'] as const;

/**
 * A stored rate: TRY per one unit of `currency`, as the `numeric(20,8)` text the
 * column holds — never a number. `sourceRef` is the TCMB `Bulten_No`, or what a
 * manual row says about itself.
 */
export const exchangeRateSchema = z.object({
  id: z.uuid(),
  currency: currencySchema,
  rateToBase: z.string(),
  rateDate: isoDaySchema,
  source: exchangeRateSourceSchema,
  sourceRef: z.string().nullable(),
  fetchedAt: z.string(),
});

export type ExchangeRate = z.infer<typeof exchangeRateSchema>;

export const EXCHANGE_RATE_SORT_FIELDS = ['rateDate', 'currency'] as const;

/**
 * A break-glass rate, typed by a person: at most twelve integer and eight
 * fractional digits, above zero, parsed as text so no digit is lost to a float.
 * TRY is the base and has no rate.
 */
export const rateToBaseInputSchema = z
  .string()
  .trim()
  .regex(/^\d{1,12}(\.\d{1,8})?$/, 'a rate is a positive decimal with at most 8 places')
  .refine((value) => /[1-9]/.test(value), 'a rate is above zero');

export const createExchangeRateInputSchema = z.object({
  currency: currencySchema.refine((currency) => currency !== 'TRY', 'TRY is the base currency'),
  rateDate: isoDaySchema,
  rateToBase: rateToBaseInputSchema,
});

export type CreateExchangeRateInput = z.infer<typeof createExchangeRateInputSchema>;

export const exchangeRateBackfillInputSchema = z
  .object({ fromDate: isoDaySchema, toDate: isoDaySchema })
  .refine((range) => range.toDate >= range.fromDate, {
    message: 'the range ends before it starts',
    path: ['toDate'],
  })
  .refine((range) => daysBetween(range.fromDate, range.toDate) <= MAX_EXCHANGE_RATE_BACKFILL_DAYS, {
    message: `a backfill covers at most ${MAX_EXCHANGE_RATE_BACKFILL_DAYS} days`,
    path: ['toDate'],
  });

export const exchangeRateBackfillRequestSchema = z.object({
  id: z.uuid(),
  fromDate: isoDaySchema,
  toDate: isoDaySchema,
  status: z.enum(EXCHANGE_RATE_BACKFILL_STATUSES),
  requestedByUserId: z.uuid(),
  createdAt: z.string(),
});

export type ExchangeRateBackfillRequest = z.infer<typeof exchangeRateBackfillRequestSchema>;

export const baseAmountBackfillInputSchema = z
  .object({
    currency: currencySchema
      .refine((currency) => currency !== 'TRY', 'a TRY donation needs no rate')
      .optional(),
    donatedFrom: isoDaySchema.optional(),
    donatedTo: isoDaySchema.optional(),
  })
  .refine(
    (range) => !range.donatedFrom || !range.donatedTo || range.donatedTo >= range.donatedFrom,
    { message: 'the range ends before it starts', path: ['donatedTo'] },
  );

export type BaseAmountBackfillInput = z.infer<typeof baseAmountBackfillInputSchema>;

/**
 * Which currencies the latest TCMB bulletin carries, so a price editor can warn
 * that a price in any other currency will not convert in reports. Codes and a
 * date only, which is why the route needs no permission.
 */
export const exchangeRateCoverageSchema = z.object({
  latestTcmbDate: isoDaySchema.nullable(),
  quoted: z.array(currencySchema),
});

export type ExchangeRateCoverage = z.infer<typeof exchangeRateCoverageSchema>;

export const exchangeRateContract = {
  list: oc
    .route({
      method: 'GET',
      path: '/exchange-rates',
      summary: 'Stored exchange rates, newest first',
    })
    .errors(guarded)
    .input(
      sortedListRequest(EXCHANGE_RATE_SORT_FIELDS, 'rateDate', 'desc')
        .omit({ search: true })
        .extend({
          currency: currencySchema.optional(),
          source: exchangeRateSourceSchema.optional(),
          rateFrom: isoDaySchema.optional(),
          rateTo: isoDaySchema.optional(),
        }),
    )
    .output(paginated(exchangeRateSchema)),

  create: oc
    .route({
      method: 'POST',
      path: '/exchange-rates',
      summary: 'Enter a rate by hand when TCMB cannot be used; never edits one',
    })
    .errors(guarded)
    .input(createExchangeRateInputSchema)
    .output(exchangeRateSchema),

  backfill: oc
    .route({
      method: 'POST',
      path: '/exchange-rates/backfill',
      summary: 'Fill a past range from the TCMB archive, in the worker',
    })
    .errors(guarded)
    .input(exchangeRateBackfillInputSchema)
    .output(exchangeRateBackfillRequestSchema),

  baseAmountBackfill: oc
    .route({
      method: 'POST',
      path: '/exchange-rates/base-amount-backfill',
      summary: 'Freeze a rate onto donations that were submitted before one existed',
    })
    .errors(guarded)
    .input(baseAmountBackfillInputSchema)
    .output(z.object({ dispatchKey: z.string() })),

  coverage: oc
    .route({ method: 'GET', path: '/exchange-rates/coverage' })
    .errors({
      UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
      TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
    })
    .output(exchangeRateCoverageSchema),
};
